import React, { useEffect, useState } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { RsaDecryptor, DecryptAttachmentBrowser } from '../../services/e2ee';
import { REACT_APP_E2EE_MEDIA_SIZE_PREVIEW_LIMIT } from '../../config';
import {
  LoadingContainer,
  ErrorContainer,
  DecryptedImage,
  DecryptedVideo,
  LargeMediaCard,
  LargeMediaIconContainer,
  LargeMediaTitle,
  LargeMediaSubtitle,
  LargeMediaButton,
  StyledVideoIcon,
  StyledImageIcon,
} from './styles';

// 1. Fast in-memory cache for fast tab-switches (Guid -> { url, mimeType })
const decryptedCache = new Map();

// 2. Persistent browser database (IndexedDB)
const DB_NAME = 'squre_e2ee_media_cache';
const STORE_NAME = 'media_blobs';

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => {
      console.error('[E2E Cache] Failed to open IndexedDB:', e.target.error);
      reject(e.target.error);
    };
  });
};

const getCachedBlob = async (guid) => {
  try {
    console.log(`[E2E Cache] Checking IndexedDB for Guid: ${guid}`);
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(guid);
      request.onsuccess = () => {
        if (request.result) {
          console.log(`[E2E Cache] Cache HIT in IndexedDB for Guid: ${guid}`);
        } else {
          console.log(`[E2E Cache] Cache MISS in IndexedDB for Guid: ${guid}`);
        }
        resolve(request.result);
      };
      request.onerror = (e) => {
        console.error(
          `[E2E Cache] Read error for Guid: ${guid}`,
          e.target.error
        );
        reject(e.target.error);
      };
    });
  } catch (err) {
    console.error('[E2E Cache] IndexedDB get error:', err);
    return null;
  }
};

const cacheBlob = async (guid, blob) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(blob, guid);
      request.onsuccess = () => {
        // console.log(
        //   `[E2E Cache] Successfully cached decrypted file in IndexedDB for Guid: ${guid}`
        // );
        resolve();
      };
      request.onerror = (e) => {
        console.error(
          `[E2E Cache] Failed to write Guid: ${guid} to IndexedDB`,
          e.target.error
        );
        reject(e.target.error);
      };
    });
  } catch (err) {
    console.error('[E2E Cache] IndexedDB put error:', err);
  }
};

const clearIndexedDB = async () => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();
      request.onsuccess = () => {
        console.log('[E2E Cache] Cleared all files from IndexedDB.');
        resolve();
      };
      request.onerror = (e) => reject(e.target.error);
    });
  } catch (err) {
    console.error('[E2E Cache] IndexedDB clear error:', err);
  }
};

const getMimeType = (attachmentType) => {
  switch (attachmentType) {
    case 0:
      return 'image/png';
    case 1:
      return 'image/jpeg';
    case 20:
      return 'video/mp4';
    default:
      return 'application/octet-stream';
  }
};

const formatSize = (kb) => {
  if (!kb) return '0 KB';
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};

const EncryptedMedia = ({ att, setAttSeleted, urlDowload }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [userRequestedPreview, setUserRequestedPreview] = useState(false);

  const limitMB = parseInt(REACT_APP_E2EE_MEDIA_SIZE_PREVIEW_LIMIT || '10', 10);
  const LIMIT_MB_KB = limitMB * 1024;
  const isLargeFile = att.Size >= LIMIT_MB_KB;

  useEffect(() => {
    let active = true;

    const loadAndDecryptMedia = async () => {
      try {
        setLoading(true);
        setError(false);

        // A. Check in-memory Cache (Fast Hit)
        if (decryptedCache.has(att.Guid)) {
          console.log(`[E2E Cache] In-Memory Cache HIT for Guid: ${att.Guid}`);
          const cached = decryptedCache.get(att.Guid);
          setMediaUrl(cached.url);
          setLoading(false);
          return;
        }

        // B. Check persistent IndexedDB Cache (Survives Refresh/Restart)
        const cachedBlob = await getCachedBlob(att.Guid);
        if (!active) return;

        if (cachedBlob) {
          const createdUrl = URL.createObjectURL(cachedBlob);
          const mimeType = getMimeType(att.AttachmentType);
          decryptedCache.set(att.Guid, { url: createdUrl, mimeType });
          setMediaUrl(createdUrl);
          setLoading(false);
          return;
        }

        // C. If large file and user hasn't clicked "Load Preview" yet, don't download
        if (isLargeFile && !userRequestedPreview) {
          setLoading(false);
          return;
        }

        // D. Fallback: Download & Decrypt
        console.log(
          `[E2E Cache] Downloading & Decrypting file from server for Guid: ${att.Guid}`
        );
        const response = await fetch(`${urlDowload}/${att.Guid}`);
        if (!response.ok) throw new Error('Failed to download attachment');
        const arrayBuffer = await response.arrayBuffer();

        const decryptedKey = await RsaDecryptor(att.EncryptionKey);
        const decryptedIv = await RsaDecryptor(att.Iv);

        const decryptedBytes = await DecryptAttachmentBrowser(
          arrayBuffer,
          decryptedIv,
          decryptedKey
        );

        if (!decryptedBytes || decryptedBytes.length === 0) {
          throw new Error('Decrypted binary data is empty');
        }

        if (active) {
          const mimeType = getMimeType(att.AttachmentType);
          const fileBlob = new Blob([decryptedBytes], { type: mimeType });
          const createdUrl = URL.createObjectURL(fileBlob);

          // Store in both memory and IndexedDB caches
          decryptedCache.set(att.Guid, { url: createdUrl, mimeType });
          await cacheBlob(att.Guid, fileBlob);

          setMediaUrl(createdUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load decrypted media preview:', err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadAndDecryptMedia();

    return () => {
      active = false;
    };
  }, [
    att.Guid,
    att.EncryptionKey,
    att.Iv,
    att.AttachmentType,
    att.Size,
    urlDowload,
    userRequestedPreview,
  ]);

  if (!mediaUrl && isLargeFile && !userRequestedPreview) {
    const isVideo = att.AttachmentType === 20;

    return (
      <LargeMediaCard>
        <LargeMediaIconContainer>
          {isVideo ? (
            <StyledVideoIcon size={40} />
          ) : (
            <StyledImageIcon size={40} />
          )}
        </LargeMediaIconContainer>
        <LargeMediaTitle>{att.Name}</LargeMediaTitle>
        <LargeMediaSubtitle>
          Large Media ({formatSize(att.Size)})
        </LargeMediaSubtitle>
        <LargeMediaButton onClick={() => setUserRequestedPreview(true)}>
          Load Preview
        </LargeMediaButton>
      </LargeMediaCard>
    );
  }

  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress color="secondary" size={30} />
      </LoadingContainer>
    );
  }

  if (error) {
    return <ErrorContainer>Failed to load secure preview</ErrorContainer>;
  }

  if (att.AttachmentType === 0 || att.AttachmentType === 1) {
    return (
      <DecryptedImage
        alt={att.Name}
        src={mediaUrl}
        onClick={() => setAttSeleted({ ...att, mediaUrl })}
      />
    );
  }

  if (att.AttachmentType === 20) {
    return (
      <DecryptedVideo controls>
        <source src={mediaUrl} type="video/mp4" />
        <p title={att.Name}>{att.Name}</p>
      </DecryptedVideo>
    );
  }

  return null;
};

export const clearDecryptedMediaCache = async () => {
  decryptedCache.forEach((item) => {
    URL.revokeObjectURL(item.url);
  });
  decryptedCache.clear();
  await clearIndexedDB();
};

export { EncryptedMedia };
